import { useState, type ReactElement } from "react";
import type { AlertLogEntry } from "@/types/tradeLog";

interface Props {
  alerts: AlertLogEntry[];
}

export function AlertsTable({ alerts }: Readonly<Props>): ReactElement {
  return (
    <section className="card overflow-hidden p-0">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <i className="ti ti-bell text-lg text-accent" />
          <h3>Webhook alerts</h3>
        </div>
        <span className="text-xs text-ink-muted">{String(alerts.length)} alerts in view</span>
      </div>
      {alerts.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ink-secondary">
          No alerts match the current filters.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <Head />
            <tbody>
              {alerts.map((a) => (
                <AlertRow key={a.id} alert={a} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Head(): ReactElement {
  return (
    <thead className="text-[10px] uppercase tracking-wider text-ink-secondary">
      <tr className="border-b border-line">
        <th className="px-3 py-2 text-left">Time</th>
        <th className="px-3 py-2 text-left">Strategy</th>
        <th className="px-3 py-2 text-left">Type</th>
        <th className="px-3 py-2 text-left">Alert name</th>
        <th className="px-3 py-2 text-left">Decision</th>
        <th className="px-3 py-2 text-left">Reason</th>
        <th className="px-3 py-2 text-right">Payload</th>
      </tr>
    </thead>
  );
}

function AlertRow({ alert }: Readonly<{ alert: AlertLogEntry }>): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-line last:border-0 hover:bg-surface-2">
        <td className="px-3 py-2.5 align-top font-mono text-xs">
          {formatHHMMSS(alert.receivedAt)}
        </td>
        <td className="px-3 py-2.5 align-top">{alert.strategyName}</td>
        <td className="px-3 py-2.5 align-top">
          <TypePill type={alert.type} />
        </td>
        <td className="px-3 py-2.5 align-top font-mono text-xs">{alert.alertName}</td>
        <td className="px-3 py-2.5 align-top">
          <DecisionPill decision={alert.decision} />
        </td>
        <td className="px-3 py-2.5 align-top text-xs text-ink-secondary">
          {alert.reason ?? <span className="text-ink-muted">—</span>}
        </td>
        <td className="px-3 py-2.5 text-right align-top">
          {alert.rawPayload !== null ? (
            <button
              type="button"
              className="btn btn-secondary px-2 py-1 text-xs"
              onClick={() => {
                setOpen((o) => !o);
              }}
            >
              <i className={`ti ${open ? "ti-chevron-up" : "ti-chevron-down"} text-xs`} />
              {open ? "Hide" : "Show"}
            </button>
          ) : (
            <span className="text-xs text-ink-muted">—</span>
          )}
        </td>
      </tr>
      {open && alert.rawPayload !== null && (
        <tr className="border-b border-line bg-surface-2 last:border-0">
          <td colSpan={7} className="px-3 py-3">
            <pre className="overflow-x-auto rounded-ctl bg-surface-1 p-3 font-mono text-[11px] text-ink">
              {pretty(alert.rawPayload)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function TypePill({ type }: Readonly<{ type: "ENTRY" | "EXIT" }>): ReactElement {
  if (type === "ENTRY")
    return (
      <span className="pill bg-success-bg text-success">
        <i className="ti ti-arrow-up text-xs" /> ENTRY
      </span>
    );
  return (
    <span className="pill bg-danger-bg text-danger">
      <i className="ti ti-arrow-down text-xs" /> EXIT
    </span>
  );
}

function DecisionPill({ decision }: Readonly<{ decision: "ACCEPTED" | "REJECTED" }>): ReactElement {
  if (decision === "ACCEPTED")
    return (
      <span className="pill bg-success-bg text-success">
        <i className="ti ti-check text-xs" /> Accepted
      </span>
    );
  return (
    <span className="pill bg-warning-bg text-warning">
      <i className="ti ti-x text-xs" /> Rejected
    </span>
  );
}

function formatHHMMSS(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function pretty(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
